/**
 * <AgMark /> — refined Aᴳ brand mark with subtle periodic-element framing.
 *
 * Design:
 *   • Thin rounded-square outline (the "element block" frame)
 *   • Centered serif "A" + small superscript "G" (existing Aᴳ system)
 *   • No subtext inside the box — the symbol itself is the brand mark.
 *
 * The component is a single SVG — scales perfectly from favicon size up
 * to hero. Two visual variants (light surface / dark surface) are
 * selected by the `variant` prop. All colors flow from props/defaults
 * so consumers can match local surface treatments.
 *
 * Usage:
 *   <AgMark size={56} />                                  default light surface
 *   <AgMark size={120} variant="dark" />                  for slate panels
 *   <AgMark size={48} variant="inverted" />               favicon / app icon
 *
 * Pure SVG, no external font files at render time — favicon-safe.
 */
import React from 'react';

const PALETTE = {
  light: {
    frame: '#0B1426',     // slate
    symbol: '#0B1426',
    accent: '#FF4E6A',    // coral G
    background: 'transparent',
  },
  dark: {
    frame: '#F5F1E8',     // ivory
    symbol: '#F5F1E8',
    accent: '#FF4E6A',
    background: 'transparent',
  },
  inverted: {
    // Solid slate tile, ivory glyph — for favicon / app icon contexts
    frame: '#F5F1E8',
    symbol: '#F5F1E8',
    accent: '#FF4E6A',
    background: '#0B1426',
  },
};

export default function AgMark({
  size = 56,
  variant = 'light',
  className,
  ariaLabel = 'The Agyekum Group',
}) {
  const p = PALETTE[variant] || PALETTE.light;

  // SVG laid out on a 100×100 viewBox. Frame inset gives us the
  // rounded-square periodic-element block. The serif glyph is rendered
  // as text so it inherits the Cormorant/Garamond family supplied by
  // the host page (or falls back to serif).
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={ariaLabel}
      className={className}
    >
      {/* Optional background fill (used by inverted favicon variant) */}
      {p.background !== 'transparent' && (
        <rect x="0" y="0" width="100" height="100" rx="14" fill={p.background} />
      )}

      {/* Rounded-square periodic-element frame */}
      <rect
        x="6"
        y="6"
        width="88"
        height="88"
        rx="10"
        fill="none"
        stroke={p.frame}
        strokeWidth="2"
      />

      {/* Subtle corner notches — laboratory tile cue, very small */}
      <line x1="6"  y1="14" x2="14" y2="14" stroke={p.frame} strokeWidth="1.25" opacity="0.5" />
      <line x1="14" y1="6"  x2="14" y2="14" stroke={p.frame} strokeWidth="1.25" opacity="0.5" />
      <line x1="86" y1="6"  x2="86" y2="14" stroke={p.frame} strokeWidth="1.25" opacity="0.5" />
      <line x1="86" y1="14" x2="94" y2="14" stroke={p.frame} strokeWidth="1.25" opacity="0.5" />

      {/* Centered serif "A" with superscript "G" — symbol is the whole mark */}
      <g
        textAnchor="middle"
        fontFamily="'Cormorant Garamond', 'Times New Roman', Georgia, serif"
        fontWeight="500"
      >
        <text
          x="48"
          y="68"
          fontSize="62"
          fill={p.symbol}
          style={{ letterSpacing: '-0.02em' }}
        >
          A
        </text>
        <text
          x="76"
          y="44"
          fontSize="24"
          fill={p.accent}
          style={{ letterSpacing: '-0.01em' }}
        >
          G
        </text>
      </g>
    </svg>
  );
}
